import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Programs: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const programs = [
    {
      title: 'Playgroup',
      age: '18 months - 2.5 years',
      description: 'Sensory exploration and basic motor skills development through safe, engaging activities.',
      features: [
        'Basic sensory play',
        'Simple motor activities',
        'Social interaction',
        'Routine building',
      ],
      icon: '🧸', // Teddy bear for comfort and play
      color: 'brand-yellow',
      gradient: 'from-brand-yellow to-brand-pink',
    },
    {
      title: 'Nursery',
      age: '2.5 - 3.5 years',
      description: 'Social skills development and creative expression through structured play activities.',
      features: [
        'Creative expression',
        'Social skills building',
        'Language development',
        'Independence fostering',
      ],
      icon: '🎨', // Paint palette for creativity
      color: 'brand-blue',
      gradient: 'from-brand-blue to-brand-green',
    },
    {
      title: 'LKG',
      age: '3.5 - 4.5 years',
      description: 'Pre-academic skills through play-based learning and structured activities.',
      features: [
        'Pre-writing skills',
        'Number concepts',
        'Pattern recognition',
        'Problem solving',
      ],
      icon: '📚', // Books for learning
      color: 'brand-green',
      gradient: 'from-brand-green to-brand-yellow',
    },
    {
      title: 'UKG',
      age: '4.5 - 5.5 years',
      description: 'School readiness and independence through comprehensive learning activities.',
      features: [
        'School readiness',
        'Advanced motor skills',
        'Leadership qualities',
        'Collaborative learning',
      ],
      icon: '🏫', // School building for UKG
      color: 'brand-purple',
      gradient: 'from-brand-purple to-brand-pink',
    },
  ];

  return (
    <section id="programs" className="p-16 bg-brand-light relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-10 right-10 w-20 h-20 bg-brand-yellow rounded-full opacity-10"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-16 h-16 bg-brand-pink rounded-full opacity-10"
          animate={{ scale: [1, 0.8, 1], y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-pink mb-6">
            Our <span className="text-brand-blue">Programs</span>
          </h2>
          <p className="text-xl text-brand-purple max-w-2xl mx-auto mb-8">
            Age-appropriate learning journeys for every child
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {programs.map((program, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
            >
              <div className="bg-brand-white rounded-3xl border-2 border-brand-blue shadow-bubble overflow-hidden transition-all duration-300 group-hover:shadow-brand group-hover:border-brand-pink/30">
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${program.gradient} py-6 px-8 text-white text-center relative overflow-hidden`}>
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-2 right-2 w-8 h-8 bg-brand-white rounded-full"></div>
                    <div className="absolute bottom-2 left-2 w-6 h-6 bg-brand-white rounded-full"></div>
                  </div>
                  <motion.div
                    className="text-4xl mb-3 relative z-10"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    {program.icon}
                  </motion.div>
                  <h3 className="text-2xl text-brand-white font-bold mb-2 relative z-10">{program.title}</h3>
                </div>
                {/* Content */}
                <div className="py-6 px-8">
                  <p className="text-base text-brand-purple mb-6 leading-relaxed font-medium">
                    {program.description}
                  </p>
                  <div className="space-y-3">
                    <h4 className="font-bold text-brand-pink text-lg mb-3 flex items-center">
                      <span className="text-brand-green mr-2">✨</span>
                      What they learn:
                    </h4>
                    <ul className="space-y-2 px-8">
                      {program.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          className="flex items-start text-sm text-brand-dark py-2"
                          initial={{ opacity: 0, x: -10 }}
                          animate={inView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.3, delay: (index * 0.1) + (featureIndex * 0.05) }}
                        >
                          <span className={`text-${program.color} font-bold mr-3 mt-0.5 text-lg`}>✓</span>
                          <span className="font-medium">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />
              </div>
            </motion.div>
          ))}
        </div>
        {/* Call to action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bg-gradient-to-r from-brand-yellow/10 to-brand-blue/10 border-2 border-brand-pink/20 rounded-3xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-brand-pink mb-4">
              Ready to Start Your Child's Journey?
            </h3>
            <p className="text-brand-purple mb-6">
              Each program is carefully designed to meet your child's developmental needs at every stage.
            </p>
            <motion.a
              href="#contact"
              className="inline-flex items-center bg-brand-pink text-brand-white px-8 py-4 rounded-bubble font-bold text-lg shadow-bubble hover:shadow-brand hover:bg-brand-yellow hover:text-brand-dark transition-all duration-300 transform hover:scale-105"
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us Today
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Programs; 